import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { useDispatch, useSelector } from 'react-redux'
import { EProgramTypes } from '@ucheba/utils/types/program'
import {
  specialitiesSearchSelectors,
  specialitiesSearchThunks,
} from '@ucheba/store/specialities/search'

import { useRouter } from 'next/router'
import { authSelectors } from '@ucheba/store/auth'
import { getProgramTypeWithChilds } from '../../bll/utils'
import { ISpecialitiesByProfProps } from './types'

interface IUseSpecialitiesByProf {
  (props: ISpecialitiesByProfProps): {
    href: string
    activeIndex: number
    setActiveIndex: Dispatch<SetStateAction<number>>
    navItems: {
      name: string
      slug: string
      value: number | number[]
    }[]
    handleClick: (e: any, slug: string) => void
    total: number
    onClickGetMoreSpecialities: () => void
  }
}

export const fetchSpecialitiesByProfession = async (
  programType: EProgramTypes,
  specialityIds: number[] | null | undefined,
  dispatch: Dispatch<any>,
  limit?: number
): Promise<any> => {
  await dispatch(
    specialitiesSearchThunks.fetch({
      data: {
        limit,
        topProgramTypes: getProgramTypeWithChilds(programType),
        ids: specialityIds,
      },
    })
  )
}

export const useSpecialitiesByProf: IUseSpecialitiesByProf = (props) => {
  const { programTypes, specialityIds, tagsId } = props

  const total = useSelector(specialitiesSearchSelectors.total)

  const [activeIndex, setActiveIndex] = useState(0)

  const dispatch = useDispatch()

  const isAuth = useSelector(authSelectors.isAuth)

  const { asPath } = useRouter()

  const href = useMemo(() => {
    if (!tagsId) return `/for-abiturients/speciality/search`

    return stringifyUrl(`/for-abiturients/speciality/search`, {
      set: {
        tagIds: tagsId,
      },
    })
  }, [tagsId])

  const navItems = useMemo(() => {
    return programTypes
      .map((item) => {
        return {
          name: item.name,
          slug: item.name,
          value: item.value,
        }
      })
      .filter((item) => {
        if (Array.isArray(item.value)) {
          return (
            item.value.includes(EProgramTypes.higher) ||
            item.value.includes(EProgramTypes.vocational)
          )
        }
        return (
          item.value === EProgramTypes.higher || item.value === EProgramTypes.vocational
        )
      })
  }, [programTypes])

  const [showMore, setShowMore] = useState(false)

  const fetch = useCallback(
    async (programType: EProgramTypes) => {
      await fetchSpecialitiesByProfession(
        programType,
        specialityIds,
        dispatch,
        !showMore ? 5 : 100
      )
    },
    [dispatch, specialityIds, showMore]
  )

  const handleClick = useCallback(
    (_, slug) => {
      const index = navItems.findIndex((item) => item.slug === slug)

      setActiveIndex(index)

      if (index === activeIndex) return

      const item = navItems.find((el) => el.slug === slug)

      if (item) {
        fetch(item.value)
      }
    },
    [navItems, activeIndex, fetch]
  )

  const onClickGetMoreSpecialities = useCallback(() => {
    fetchSpecialitiesByProfession(
      navItems[activeIndex].value,
      specialityIds,
      dispatch,
      100
    )
    setShowMore(true)
  }, [activeIndex, dispatch, navItems, specialityIds])

  return {
    href,
    activeIndex,
    setActiveIndex,
    navItems,
    handleClick,
    total,
    onClickGetMoreSpecialities,
  }
}
